import {DatasetResponse} from '@backend';
import {ResourceResponse} from '@middleware';
import {SearchResponse} from '@models/search';
import {TileInfo, TileType} from '@models/tile-info';

/** generateTileList: Takes a list of various response objects, and generates a tileInfo list.  */
export const generateTileList = (searchResponse: SearchResponse[]): TileInfo[] => {
  return searchResponse
    .filter(result => result?.name && result.id)
    .map(result => {
      const tileInfo: TileInfo = {
        name: result.name,
        id: result.id,
        type: inferObjectType(result),
        icon: '',
        icon_alt: '',
        organization:
          'storage_organization' in result && result.storage_organization?.name
            ? result.storage_organization.name
            : (result.creator?.organization?.id ?? ''),
        event_metadata: {
          location: '',
          starts: '',
          ends: '',
        },
        show_organization_logo: false,
      };
      if ('show_creator_organization_logo' in result) {
        tileInfo.show_organization_logo = result.show_creator_organization_logo;
      }
      if ('show_storage_organization_logo' in result) {
        tileInfo.show_organization_logo = result.show_storage_organization_logo;
      }
      if ('icon' in result) {
        tileInfo.icon = result.icon.symbol;
        tileInfo.icon_alt = result.icon.name;
      }
      if ('event_metadata' in result) {
        tileInfo.event_metadata.starts = result.event_metadata.starts;
        tileInfo.event_metadata.ends = result.event_metadata.ends;
        tileInfo.event_metadata.location = result.event_metadata.location;
      }
      return tileInfo;
    });
};
/** generateDatasetTilesFromResponse: Generates a list of tiles given a DatasetResponse[] type
 *  Note: This is specifically for datasets fetched on a private project page. */
export const generateDatasetTilesFromResponse = (datasets: DatasetResponse[]): TileInfo[] => {
  return datasets
    .filter(d => d?.id && d.name && d.scheduled_for_deletion_on == null)
    .map(d => ({
      id: d.id!,
      name: d.name,
      type: 'Dataset' as const,
      organization: d.storage_organization?.name ?? '',
      icon: 'database',
      icon_alt: 'Dataset',
      event_metadata: {
        location: '',
        starts: '',
        ends: '',
      },
      show_organization_logo: d.show_storage_organization_logo ?? false,
    }));
};

/** inferObjectType: Returns a valid type for a given object */
export const inferObjectType = (result?: SearchResponse): TileType => {
  // FIXME: Should this really return "Project" if the given object is falsy?
  if (!result) return 'Project';

  if (Object.hasOwn(result, 'resource_type')) {
    const resource = result as ResourceResponse;
    return resource.resource_type === 'Generic' ? 'Resource' : resource.resource_type;
  }

  if (Object.hasOwn(result, 'dataset_type')) return 'Dataset';

  return 'Project';
};
